import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, CalendarCheck2 } from 'lucide-react';
import { toast } from 'sonner';
import { meetingsAPI } from '../lib/api';
import MyMeetingsTab from '../components/meetings/MyMeetingsTab';
import ToReviewTab from '../components/meetings/ToReviewTab';
import TeamMeetingsTab from '../components/meetings/TeamMeetingsTab';
import AllMeetingsTab from '../components/meetings/AllMeetingsTab';
import ActionItemsTab from '../components/meetings/ActionItemsTab';
import ReportsTab from '../components/meetings/ReportsTab';
import VisitsToTagTab from '../components/meetings/VisitsToTagTab';
import AdminTab from '../components/meetings/AdminTab';
import MeetingDetailView from '../components/meetings/MeetingDetailView';

export default function MeetingsPage() {
  const { meetingId } = useParams();
  const [capabilities, setCapabilities] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [tab, setTab] = useState('mine');

  useEffect(() => {
    meetingsAPI.capabilities()
      .then((res) => {
        setCapabilities(res.data);
        if (!res.data.can_log && res.data.has_reviews) setTab('review');
        else if (!res.data.can_log && res.data.is_manager) setTab('team');
        else if (!res.data.can_log && res.data.can_view_people_report) setTab('reports');
      })
      .catch((e) => {
        if (e.response?.status === 403) setAccessDenied(true);
        else toast.error('Failed to load Visits & Meetings');
      });
  }, []);

  if (accessDenied) {
    return (
      <Card className="rounded-sm" data-testid="meetings-access-denied">
        <CardContent className="p-8 text-center text-muted-foreground">You don't have access to Visits & Meetings.</CardContent>
      </Card>
    );
  }

  if (!capabilities) {
    return <div className="flex justify-center py-20" data-testid="meetings-page-loading"><Loader2 className="animate-spin" /></div>;
  }

  if (meetingId) {
    return (
      <div className="space-y-6" data-testid="meetings-page">
        <MeetingDetailView meetingId={meetingId} />
      </div>
    );
  }

  const hasAnyTab = capabilities.can_log || capabilities.has_reviews || capabilities.is_manager
    || capabilities.can_view_people_report || capabilities.can_admin;

  return (
    <div className="space-y-6" data-testid="meetings-page">
      <div className="flex items-center gap-2">
        <CalendarCheck2 className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-semibold">Visits & Meetings</h1>
      </div>

      {!hasAnyTab ? (
        <Card className="rounded-sm" data-testid="meetings-phase2-notice">
          <CardContent className="p-8 text-center text-muted-foreground">
            You don't have access to any Visits & Meetings views yet.
          </CardContent>
        </Card>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto w-full sm:w-auto overflow-x-auto">
            {capabilities.can_log && <TabsTrigger value="mine" data-testid="meetings-tab-mine">My Meetings</TabsTrigger>}
            {capabilities.has_reviews && <TabsTrigger value="review" data-testid="meetings-tab-review">To Review</TabsTrigger>}
            {capabilities.is_manager && <TabsTrigger value="team" data-testid="meetings-tab-team">Team</TabsTrigger>}
            {capabilities.can_admin && <TabsTrigger value="all" data-testid="meetings-tab-all">All Meetings</TabsTrigger>}
            {capabilities.can_log && <TabsTrigger value="actions" data-testid="meetings-tab-actions">Action Items</TabsTrigger>}
            {capabilities.can_log && <TabsTrigger value="to_tag" data-testid="meetings-tab-to-tag">Visits to Tag</TabsTrigger>}
            {capabilities.can_view_people_report && <TabsTrigger value="reports" data-testid="meetings-tab-reports">Reports</TabsTrigger>}
            {capabilities.can_admin && <TabsTrigger value="admin" data-testid="meetings-tab-admin">Admin</TabsTrigger>}
          </TabsList>
          {capabilities.can_log && <TabsContent value="mine"><MyMeetingsTab /></TabsContent>}
          {capabilities.has_reviews && <TabsContent value="review"><ToReviewTab /></TabsContent>}
          {capabilities.is_manager && <TabsContent value="team"><TeamMeetingsTab /></TabsContent>}
          {capabilities.can_admin && <TabsContent value="all"><AllMeetingsTab /></TabsContent>}
          {capabilities.can_log && <TabsContent value="actions"><ActionItemsTab isManager={capabilities.is_manager} /></TabsContent>}
          {capabilities.can_log && <TabsContent value="to_tag"><VisitsToTagTab capabilities={capabilities} /></TabsContent>}
          {capabilities.can_view_people_report && <TabsContent value="reports"><ReportsTab capabilities={capabilities} /></TabsContent>}
          {capabilities.can_admin && <TabsContent value="admin"><AdminTab /></TabsContent>}
        </Tabs>
      )}
    </div>
  );
}
